import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { setActiveFilters } from '../../../redux/actions/filterActions';
import { fetchCategories } from '../../../redux/actions/categoryActions';

const ProductsMenu = ({ onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = window.innerWidth < 768;
  
  // Obtener categorías y estado de carga
  const { categories, error } = useSelector(state => state.categories);

  // Cargar categorías cuando el componente se monta
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  const handleCategoryClick = async (category) => {
    // Solo navegar si es una categoría sin subcategorías
    if (!category.subcategories || category.subcategories.length === 0) {
      const filters = {
        category: [category.id],
      };
      await dispatch(setActiveFilters(filters));
      navigate('/products');
      setIsOpen(false);
      if (onClose) onClose();
    } else {
      // Si tiene subcategorías, solo expandir/colapsar
      toggleCategoryExpansion(category.id);
    }
  };

  const toggleCategoryExpansion = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const CategoryMenuItem = ({ category, depth = 0 }) => {
    const [isHovered, setIsHovered] = useState(false);
    const hasSubcategories = category.subcategories && category.subcategories.length > 0;
    const isExpanded = expandedCategories[category.id];

    // Versión para escritorio con hover
    if (!isMobile) {
      return (
        <div
          className="relative"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <button
            onClick={() => handleCategoryClick(category)}
            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100 flex items-center justify-between group"
          >
            <span className="text-gray-700 group-hover:text-gray-900">{category.name}</span>
            {hasSubcategories && (
              <ChevronRight className="h-4 w-4 text-gray-400 group-hover:text-gray-600" />
            )}
          </button>

          {isHovered && hasSubcategories && (
            <div 
              className="absolute left-full top-0 bg-white shadow-lg rounded-r-lg min-w-[200px] py-1"
              style={{ marginLeft: '1px' }}
            >
              {category.subcategories.map(subcategory => (
                <CategoryMenuItem 
                  key={subcategory.id} 
                  category={subcategory}
                  depth={depth + 1}
                />
              ))}
            </div>
          )}
        </div>
      );
    }
    
    // Versión para móvil con expansión
    return (
      <div className="w-full">
        <button
          onClick={() => handleCategoryClick(category)}
          className={`
            w-full text-left px-3 py-3 text-gray-700 hover:bg-gray-50
            flex items-center justify-between transition-colors duration-150
            ${hasSubcategories ? 'font-medium' : 'font-normal'}
            ${depth > 0 ? `pl-${depth * 4 + 6}` : ''}
            ${isExpanded ? 'bg-gray-50' : ''}
            ${depth === 0 ? 'border-b border-gray-100' : ''}
          `}
        >
          <span className={`${isExpanded ? 'text-gray-900' : 'text-gray-700'}`}>
            {category.name}
          </span>
          {hasSubcategories && (
            isExpanded ? 
            <ChevronDown className="h-5 w-5 text-gray-500" /> :
            <ChevronRight className="h-5 w-5 text-gray-500" />
          )}
        </button>

        {isExpanded && hasSubcategories && (
          <div className="w-full bg-gray-50">
            {category.subcategories.map(subcategory => (
              <CategoryMenuItem 
                key={subcategory.id} 
                category={subcategory}
                depth={depth + 1}
              />
            ))}
          </div>
        )}
      </div>
    );
  };

  // Manejar estados de carga y error
  if (error) {
    console.error('Error loading categories:', error);
  }

  if (!categories || categories.length === 0) {
    return (
      <div className="relative">
        <button className={`
          text-gray-700 hover:text-gray-900 font-medium
          ${isMobile ? 'text-lg py-2 w-full text-left' : ''}
        `}>
          Catálogo
        </button>
      </div>
    );
  }

  return (
    <div
      className={`relative ${isMobile ? 'w-full' : ''}`}
      onMouseEnter={() => !isMobile && setIsOpen(true)}
      onMouseLeave={() => !isMobile && setIsOpen(false)}
    >
      <button 
        className={`
          text-gray-700 hover:text-gray-900 font-medium flex items-center
          ${isMobile ? 'text-lg py-2 w-full justify-between' : ''}
        `}
        onClick={() => isMobile && setIsOpen(!isOpen)}
      >
        <span>Catálogo</span>
        {isMobile && (
          isOpen ? 
          <ChevronDown className="h-5 w-5 text-gray-500" /> : 
          <ChevronRight className="h-5 w-5 text-gray-500" />
        )}
      </button>

      {isOpen && (
        <div className={`
          bg-white shadow-lg rounded-lg py-2 z-50
          ${isMobile ? 
            'relative w-full mt-2 border border-gray-100 overflow-hidden' : 
            'absolute top-full left-0 min-w-[250px]'}
        `}>
          <button
            className={`
              w-full text-left px-4 py-3 font-medium text-gray-900 hover:bg-gray-50
              ${isMobile ? 'border-b border-gray-100' : ''}
            `}
            onClick={() => {
              dispatch(setActiveFilters({ category: [], tags: [] }));
              navigate('/products');
              setIsOpen(false);
              if (onClose) onClose();
            }}
          >
            Ver todas las categorías
          </button>

          <div className="my-1" />

          {categories.map(category => (
            <CategoryMenuItem 
              key={category.id} 
              category={category}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsMenu;